import React, { useState } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';
import { DropZone } from './file-upload/DropZone';
import { UploadProgress } from './file-upload/UploadProgress';
import { UploadedFile } from './file-upload/UploadedFile';

interface FileUploadProps {
  buttonText?: string;
}

export const FileUpload = ({ buttonText = "Encrypt File" }: FileUploadProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isEncrypting, setIsEncrypting] = useState(false);
  const { toast } = useToast();

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    await handleFiles(files);
  };

  const handleFileInput = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    await handleFiles(files);
  };

  const handleOneDriveSelect = () => {
    toast({
      title: "OneDrive Connection",
      description: "Connecting to OneDrive... (Feature coming soon)",
    });
  };

  const handleFiles = async (files: File[]) => {
    if (files.length === 0) return;

    setIsUploading(true);
    setUploadProgress(0);

    const interval = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + 10;
      });
    }, 200);

    setTimeout(() => {
      clearInterval(interval);
      setIsUploading(false);
      setUploadProgress(0);
      setUploadedFile(files[0]);
      toast({
        title: "Upload complete",
        description: `Successfully uploaded ${files[0].name}`,
      });
    }, 2000);
  };

  const handleEncryption = async () => {
    if (!uploadedFile) return;

    setIsEncrypting(true);
    
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      toast({
        title: "Encryption complete",
        description: "Your file has been encrypted successfully",
      });

      setUploadedFile(null);
    } catch (error) {
      toast({
        title: "Encryption failed",
        description: "There was an error encrypting your file",
        variant: "destructive",
      });
    } finally {
      setIsEncrypting(false);
    }
  };

  return (
    <div className="w-full max-w-xl mx-auto p-6">
      {!uploadedFile ? (
        <div
          className={cn(
            "relative h-64 rounded-lg border-2 border-dashed border-gray-300 transition-all duration-300",
            "flex flex-col items-center justify-center space-y-4 p-6",
            "bg-white/50 backdrop-blur-sm",
            isDragging && "border-primary bg-primary/5",
            isUploading && "border-primary"
          )}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {!isUploading ? (
            <DropZone
              isDragging={isDragging}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onFileInput={handleFileInput}
              onOneDriveSelect={handleOneDriveSelect}
            />
          ) : (
            <UploadProgress uploadProgress={uploadProgress} />
          )}
        </div>
      ) : (
        <UploadedFile
          fileName={uploadedFile.name}
          isEncrypting={isEncrypting}
          onEncrypt={handleEncryption}
          buttonText={buttonText}
        />
      )}
    </div>
  );
};